// My Classes, for teachers and students: the classes they teach (each opens its roster)
// and the classes they are a student in, with the consent screen in front of practice when
// their school still needs it.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { MyClass, MyClasses, MyConsent, acceptMyConsent, fetchMyClasses } from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  pageStyle,
  primaryButtonStyle,
  roleBadgeStyle,
  sectionHeadingStyle,
  subtitleStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('MyClassesPage');

interface ConsentProps {
  consent: MyConsent;
  onAccepted: (consent: MyConsent) => void;
}

/** The consent screen. A school can ask students to accept for themselves; otherwise a
 *  guardian or the school gives it, and the student can only be told who to ask. */
export const ConsentCard: React.FC<ConsentProps> = ({ consent, onAccepted }) => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      onAccepted(await acceptMyConsent());
    } catch (err) {
      logger.error('Could not accept consent', err);
      setError(errorMessage(err, t('classes.couldNotSaveConsent')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="tour-consent-card"
      style={{ ...cardStyle, borderColor: '#fcd34d', background: '#fffbeb' }}
      aria-labelledby="consent-heading"
    >
      <h2 id="consent-heading" style={{ ...sectionHeadingStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShieldCheck size={18} /> {t('classes.consentNeededTitle')}
      </h2>
      {error && <div style={errorStyle} role="alert">{error}</div>}
      {consent.can_self_consent ? (
        <>
          <p style={{ fontSize: '0.9rem', color: T.text, lineHeight: 1.6, margin: '0 0 0.75rem' }}>
            {t('classes.selfConsentText')}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
            {t('classes.selfConsentCheckbox')}
          </label>
          <button type="button" style={primaryButtonStyle} disabled={!agreed || saving} onClick={accept}>
            {saving ? t('classes.saving') : t('classes.acceptConsent')}
          </button>
        </>
      ) : (
        <p style={{ fontSize: '0.9rem', color: T.text, lineHeight: 1.6, margin: 0 }}>
          {t(`classes.consentWaiting.${consent.consent_type ?? 'school'}`)}
        </p>
      )}
    </section>
  );
};

const ClassCard: React.FC<{ item: MyClass; linkToRoster: boolean }> = ({ item, linkToRoster }) => {
  const { t } = useTranslation();
  const meta = [item.subject, item.grade_range, item.school_year].filter(Boolean).join(' · ');
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: '1rem', color: T.text }}>{item.name}</strong>
        <span style={roleBadgeStyle(item.role)}>
          {item.role === 'shared' && item.grant_scope
            ? t(`studentAdmin.sharing.scopes.${item.grant_scope}`)
            : t(`classes.roles.${item.role}`)}
        </span>
      </div>
      {meta && <p style={{ ...hintStyle, marginTop: 4 }}>{meta}</p>}
      {item.role === 'shared' && item.grant_expires_at && (
        <p style={{ ...hintStyle, marginTop: 4 }}>
          {t('classes.sharedUntil', { date: new Date(item.grant_expires_at).toLocaleDateString(uiLocale()) })}
        </p>
      )}
    </>
  );
  const style: React.CSSProperties = { ...cardStyle, marginBottom: 0, display: 'block', textDecoration: 'none' };
  return linkToRoster ? (
    <Link to={`/classes/${item.id}`} style={style}>
      {body}
    </Link>
  ) : (
    <div style={style}>{body}</div>
  );
};

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '1rem',
  marginBottom: '1.5rem',
};

const MyClassesPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<MyClasses | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchMyClasses());
    } catch (err) {
      logger.error('Failed to load my classes', err);
      setError(t('classes.couldNotLoadClasses'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle} role="alert">{error}</div>
      </div>
    );
  }
  if (!data) return <LoadingIndicator message={t('classes.loading')} />;

  const teaching = data.classes.filter(c => c.role === 'teacher' || c.role === 'co_teacher');
  const shared = data.classes.filter(c => c.role === 'shared');
  const studying = data.classes.filter(c => c.role === 'student');

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 id="tour-page-title" style={titleStyle}>
          {t('sidebar.myClasses')}
        </h1>
        <p style={subtitleStyle}>{t('classes.myClassesSubtitle')}</p>
      </div>

      {data.consent.required && !data.consent.granted && (
        <ConsentCard consent={data.consent} onAccepted={consent => setData({ ...data, consent })} />
      )}

      {data.classes.length === 0 && (
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
          <GraduationCap size={20} color={T.muted} />
          <p style={{ ...hintStyle, margin: 0 }}>{t('classes.noMyClasses')}</p>
        </div>
      )}

      {teaching.length > 0 && (
        <>
          <h2 style={sectionHeadingStyle}>{t('classes.classesITeach')}</h2>
          <div style={grid}>
            {teaching.map(c => (
              <ClassCard key={c.id} item={c} linkToRoster />
            ))}
          </div>
        </>
      )}

      {shared.length > 0 && (
        <>
          <h2 style={sectionHeadingStyle}>{t('classes.sharedWithMe')}</h2>
          <div style={grid}>
            {shared.map(c => (
              <ClassCard key={c.id} item={c} linkToRoster />
            ))}
          </div>
        </>
      )}

      {studying.length > 0 && (
        <>
          <h2 style={sectionHeadingStyle}>{t('classes.classesIAmIn')}</h2>
          <div style={grid}>
            {studying.map(c => (
              <ClassCard key={c.id} item={c} linkToRoster={false} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MyClassesPage;
